/**
 * The Owners page's row source: billing entities and portal accounts, joined.
 *
 * Owners used to be split across two screens that each read only half the
 * picture — `lessors` on Owners, `profiles` + `user_roles` on Roles & access —
 * so the same person could appear on one and be missing from the other. This
 * hook reads both and hands back one row per owner, with the missing halves
 * called out rather than hidden. The join rule itself lives in
 * `@/lib/owner-directory`, which is pure and tested.
 *
 * Nothing here writes. Both queries are the ones the two pages already ran, and
 * every edit still goes back to whichever record it came from.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchAdminProfilesDirectory,
  type AdminProfileDirectoryRow,
} from "@/lib/fetch-admin-profiles-directory";
import {
  filterOwnerDirectory,
  mergeOwnerDirectory,
  type OwnerDirectoryRow,
} from "@/lib/owner-directory";
import { useOwnersList, type OwnerListRow } from "@/hooks/v2/useOwnerDetail";
import type { QueryPollOptions } from "@/lib/query-poll";

/** One owner row, with the enriched lessor record when there is one. */
export type OwnerDirectoryListRow = OwnerDirectoryRow<OwnerListRow>;

/**
 * Search is applied IN MEMORY, deliberately.
 *
 * `useOwnersList(search)` filters `lessors` server-side, which would be the
 * cheaper option — but it breaks the join in two ways. A lessor filtered out by
 * the server would leave its linked account looking like an owner with no
 * billing entity, and an account-only owner never passes through a `lessors`
 * query at all, so they could never be found by typing their name. Owner counts
 * here are tens, not thousands, so filtering the merged set costs nothing and
 * keeps every row's link state honest.
 */
export function useOwnersDirectory(
  search?: string,
  options: { enabled?: boolean; poll?: QueryPollOptions } = {}
) {
  const { enabled = true, poll } = options;

  const ownersQuery = useOwnersList(undefined, enabled, poll);
  const accountsQuery = useQuery<AdminProfileDirectoryRow[]>({
    // Same key the roles directory uses, so both stay in step and an edit made
    // through either surface invalidates one shared cache entry.
    queryKey: ["admin-role-mgmt-users"],
    enabled,
    ...poll,
    queryFn: fetchAdminProfilesDirectory,
  });

  const allRows = useMemo(
    () =>
      mergeOwnerDirectory<OwnerListRow>(ownersQuery.data, accountsQuery.data),
    [ownersQuery.data, accountsQuery.data]
  );

  const rows = useMemo(
    () => filterOwnerDirectory(allRows, search ?? ""),
    [allRows, search]
  );

  return {
    /** Owners matching the current search. */
    rows,
    /** Every owner, unfiltered by search. The Owners page drops test owners
     *  from it before its KPI tiles count it (COL-485). */
    allRows,
    /** The raw halves, for the surfaces that still need one side only. */
    lessors: ownersQuery.data,
    accounts: accountsQuery.data,
    /**
     * Loading only while the BILLING half is still in flight. The accounts
     * query decorates rows with portal state; blocking the table on it would
     * hide owners we can already show.
     */
    isLoading: ownersQuery.isLoading,
    /** The account half still in flight. The table doesn't wait on it, but
     *  anything that decides from account fields (the Owners page's test-owner
     *  filter, COL-485) can. */
    accountsLoading: accountsQuery.isLoading,
    /** True once the account half has failed — rows render, portal state is degraded. */
    accountsFailed: accountsQuery.isError,
    error: ownersQuery.error,
  };
}
